/**
 * SahibDrawer — the right-slide drawer body for the SahibCompanion.
 * Extracted in v167.x to keep the main file lean (≤200 lines).
 *
 * Pure-ish: receives state + callbacks from the parent. The only side
 * effect it owns is auto-scrolling the chat to the bottom whenever
 * the active history grows.
 */
import React, { useEffect, useRef } from 'react';
import { X, Send, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import {
  ToolConfirmCard, RunningPlanCard, PendingPlanCard,
  JournalInputPanel, MessageStatusBadges,
} from './parts';

const PERSONA_GLYPH = {
  yusuf: '👑', khadija: '💎', mohammed: '⚙', yacob: '✦',
  maryam: '⚖', nouh: '🛡', fatima: '✶', ibrahim: '◈',
  sulaiman: '◐', musa: '⚖', isa: '✦', dawud: '🛡', harun: '◉',
  shuaib: '✦', idris: '✦', wujdan: '♡', iqra: '✶', bilqis: '◈',
  yunus: '⚓', ayyub: '✚', ismail: '◬', hud: '✈',
  salih: '◧', adam: '✿', zakariyya: '✦', ilyas: '⚡',
};

const SUGGESTIONS = [
  'How many entities?',
  'Take me to documents',
  'What expires in 30 days?',
  'Log feedback: the dashboard is great',
];

function DrawerHeader({ meta, activeMeta, onClose }) {
  return (
    <div className="px-5 py-4 border-b border-slate-200 dark:border-white/10 flex items-center justify-between">
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-[0.28em] font-bold text-[#5E1F8B] dark:text-[#8B3FC5]">
          {meta.page_label} · AI Companion
        </div>
        <div className="display text-lg font-bold mt-0.5 truncate text-slate-900 dark:text-white">
          {activeMeta?.english_name}{' '}
          <span className="text-[#5E1F8B] dark:text-[#8B3FC5]" dir="rtl">{activeMeta?.arabic_name}</span>
        </div>
        <div className="text-[11px] text-slate-500 dark:text-white/55 truncate">{activeMeta?.english_role}</div>
      </div>
      <button
        onClick={onClose}
        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/10 transition"
        data-testid="sahib-close"
      >
        <X size={16}/>
      </button>
    </div>
  );
}

function PersonaTabs({ tabPersonas, activePersona, onSelect }) {
  return (
    <div className="px-3 pt-3 flex items-center gap-1.5 flex-wrap">
      {tabPersonas.map((p) => {
        const isActive = p.id === activePersona;
        return (
          <button
            key={p.id}
            onClick={() => onSelect(p.id)}
            data-testid={`sahib-tab-${p.id}`}
            className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 h-7 rounded-full transition-all
              ${isActive
                ? 'bg-[#0A0A0A] text-[#8B3FC5] dark:bg-[#8B3FC5]/15'
                : 'border border-slate-200 dark:border-white/15 text-slate-600 dark:text-white/65 hover:border-[#8B3FC5]/50'}`}
          >
            <span className="mr-1">{PERSONA_GLYPH[p.id] || '✦'}</span>
            {p.english_name}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({ activeMeta }) {
  return (
    <div className="text-center pt-8">
      <Sparkles size={20} className="mx-auto text-[#5E1F8B] dark:text-[#8B3FC5] mb-3"/>
      <div className="text-sm text-slate-600 dark:text-white/70 max-w-xs mx-auto">
        Ask anything about this page. {activeMeta?.english_name} knows your live tenant data.
      </div>
    </div>
  );
}

function SuggestionChips({ onPick, inputRef }) {
  return (
    <div className="pt-2">
      <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-slate-400 dark:text-white/40 mb-2 text-center">
        Try one
      </div>
      <div className="flex flex-wrap gap-1.5 justify-center">
        {SUGGESTIONS.map((q) => (
          <button
            key={q}
            data-testid={`sahib-suggest-${q.slice(0, 14).replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            onClick={() => { onPick(q); setTimeout(() => inputRef.current?.focus(), 50); }}
            className="text-[10px] px-2 h-6 rounded-full border border-[#8B3FC5]/40 text-[#7A5A00] dark:text-[#8B3FC5] hover:bg-[#FFF8E5] dark:hover:bg-[#3a2f0a]/40 transition"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageRow({ msg, idx, busy, activePersona, actions, history }) {
  return (
    <div>
      <div
        className={`text-sm leading-relaxed whitespace-pre-wrap
          ${msg.role === 'user'
            ? 'ml-8 bg-slate-100 dark:bg-white/[0.04] rounded-lg p-3 text-slate-800 dark:text-white/85'
            : 'mr-8 bg-[#FFF8E5]/70 dark:bg-[#3a2f0a]/40 border border-[#8B3FC5]/30 rounded-lg p-3 text-slate-900 dark:text-white/90'}`}
      >
        {msg.text}
      </div>
      {msg.pendingTool && (
        <ToolConfirmCard
          pending={msg.pendingTool}
          busy={busy}
          onApprove={() => actions.confirmToolCall(msg.pendingTool.tool, msg.pendingTool.args, idx, activePersona)}
          onDecline={() => actions.declineToolCall(idx, activePersona, history)}
        />
      )}
      {msg.runningPlan && <RunningPlanCard plan={msg.runningPlan}/>}
      {msg.pendingPlan && (
        <PendingPlanCard
          plan={msg.pendingPlan}
          busy={busy}
          onApprove={() => actions.confirmPlan(msg.pendingPlan.plan_id, idx, activePersona, history)}
          onDecline={() => actions.declinePlan(idx, activePersona, history)}
        />
      )}
      <MessageStatusBadges msg={msg}/>
    </div>
  );
}

export default function SahibDrawer({
  meta, activeMeta, activePersona, tabPersonas,
  activeMsgs, history, busy, msg, setMsg,
  shotData, shotBusy, journalSent,
  onClose, onSelectPersona, actions,
  inputRef,
}) {
  const scrollRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, activePersona, busy]);

  const hasUserSpoken = activeMsgs.some((m) => m.role === 'user');

  return (
    <div
      data-testid="sahib-drawer"
      className="fixed inset-0 z-[80] flex justify-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"/>
      <aside
        className="relative w-full max-w-[440px] h-full bg-white dark:bg-[#0d0d0d]
                   border-l border-slate-200 dark:border-white/10 flex flex-col shadow-2xl"
      >
        <DrawerHeader meta={meta} activeMeta={activeMeta} onClose={onClose}/>
        <PersonaTabs
          tabPersonas={tabPersonas}
          activePersona={activePersona}
          onSelect={onSelectPersona}
        />

        <div className="px-5 pt-3 pb-2 text-[11px] italic text-slate-500 dark:text-white/55 leading-relaxed">
          {activeMeta?.tagline}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {activeMsgs.length === 0 && <EmptyState activeMeta={activeMeta}/>}
          {activePersona === 'iqra' && !hasUserSpoken && (
            <SuggestionChips onPick={setMsg} inputRef={inputRef}/>
          )}
          {activeMsgs.map((m, i) => (
            <MessageRow
              key={`msg-${i}-${m.role}`}
              msg={m}
              idx={i}
              busy={busy}
              activePersona={activePersona}
              actions={actions}
              history={history}
            />
          ))}
          {busy && (
            <div className="mr-8 text-xs text-slate-500 dark:text-white/45 flex items-center gap-2">
              <Loader2 size={12} className="animate-spin"/>
              {activeMeta?.english_name} is thinking…
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 dark:border-white/10 p-3 flex flex-col gap-2">
          {activePersona === 'iqra' && (
            <JournalInputPanel
              shotData={shotData}
              shotBusy={shotBusy}
              journalSent={journalSent}
              busy={busy}
              msg={msg}
              onCapture={actions.captureScreenshot}
              onClearShot={() => actions.setShotData(null)}
              onSend={() => actions.sendJournalEntry(msg, shotData)}
            />
          )}

          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); actions.send(msg); }
              }}
              placeholder={`Ask ${activeMeta?.english_name}…`}
              rows={2}
              className="flex-1 resize-none text-sm bg-transparent border border-slate-200 dark:border-white/15
                         rounded-md px-3 py-2 focus:outline-none focus:border-[#8B3FC5]
                         text-slate-900 dark:text-white placeholder:text-slate-400"
              data-testid="sahib-input"
            />
            <button
              onClick={() => actions.send(msg)}
              disabled={busy || !msg.trim()}
              data-testid="sahib-send"
              className="ve-btn-primary px-4 py-2 text-xs disabled:opacity-40"
            >
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            </button>
          </div>
        </div>

        <div className="px-3 pb-3 text-[10px] text-slate-400 dark:text-white/35 leading-relaxed">
          {activeMeta?.english_name} is grounded in your live tenant data and logged to the audit trail.
          <span className="inline-flex items-center gap-1 ml-1 text-[#5E1F8B] dark:text-[#8B3FC5]">
            AES-256 encrypted <ArrowRight size={9}/>
          </span>
        </div>
      </aside>
    </div>
  );
}

// Re-export inputRef + drawer for components that want to mount their own input ref.
export { PERSONA_GLYPH };
